import { useEffect, useState } from 'react';

import { useDebounceEffect } from 'ahooks';

import { CURRENCY_FIELDS, formatInputValue, parseNumber } from './cashFlow';

import type { CashFlowFieldName, CashFlowValues, PurchaseMethod } from './cashFlow';

const PARAM_PREFIX = 'cf_';
const METHOD_PARAM = `${PARAM_PREFIX}method`;
const DEFAULT_PURCHASE_METHOD: PurchaseMethod = 'mortgage';

const readValuesFromUrl = (params: URLSearchParams, defaults: CashFlowValues): CashFlowValues => {
  const values = { ...defaults };
  (Object.keys(defaults) as Array<CashFlowFieldName>).forEach((field) => {
    const param = params.get(`${PARAM_PREFIX}${field}`);
    if (param !== null && parseNumber(param) !== undefined) {
      values[field] = formatInputValue(param, CURRENCY_FIELDS.includes(field));
    }
  });
  return values;
};

const writeStateToUrl = (values: CashFlowValues, purchaseMethod: PurchaseMethod, defaults: CashFlowValues) => {
  const url = new URL(window.location.href);
  (Object.keys(defaults) as Array<CashFlowFieldName>).forEach((field) => {
    const parsed = parseNumber(values[field]);
    if (parsed === undefined || parsed === parseNumber(defaults[field])) {
      url.searchParams.delete(`${PARAM_PREFIX}${field}`);
    } else {
      url.searchParams.set(`${PARAM_PREFIX}${field}`, String(parsed));
    }
  });
  if (purchaseMethod === DEFAULT_PURCHASE_METHOD) {
    url.searchParams.delete(METHOD_PARAM);
  } else {
    url.searchParams.set(METHOD_PARAM, purchaseMethod);
  }
  if (url.href !== window.location.href) {
    window.history.replaceState(window.history.state, '', url);
  }
};

const useCashFlowUrlState = (defaults: CashFlowValues) => {
  const [values, setValues] = useState(defaults);
  const [purchaseMethod, setPurchaseMethod] = useState<PurchaseMethod>(DEFAULT_PURCHASE_METHOD);
  const [isUrlRead, setIsUrlRead] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setValues(readValuesFromUrl(params, defaults));
    if (params.get(METHOD_PARAM) === 'cash') {
      setPurchaseMethod('cash');
    }
    setIsUrlRead(true);
  }, [defaults]);

  useDebounceEffect(
    () => {
      if (isUrlRead) {
        writeStateToUrl(values, purchaseMethod, defaults);
      }
    },
    [values, purchaseMethod, isUrlRead, defaults],
    { wait: 300 },
  );

  return { values, setValues, purchaseMethod, setPurchaseMethod };
};

export default useCashFlowUrlState;
